import React, { useState } from "react";
import { AlertTriangle, X, Loader2, Eye, EyeOff } from "lucide-react";

export function Btn({ variant = "primary", className = "", children, as: As = "button", ...props }) {
  const base =
    "inline-flex items-center justify-center gap-2 rounded-[10px] px-4 py-2 text-sm font-semibold transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-offset-0 disabled:opacity-50 disabled:pointer-events-none";
  const variants = {
    primary: "bg-[color:var(--navy)] text-white hover:bg-[color:var(--navy-deep)] focus:ring-[color:var(--turmeric)] shadow-[0_1px_2px_rgba(11,18,35,.12)]",
    secondary: "border border-[color:var(--line)] bg-[color:var(--card)] text-[color:var(--ink)] hover:bg-[color:var(--turmeric-soft)] hover:border-[color:var(--turmeric)] focus:ring-[color:var(--turmeric)]",
    accent: "bg-[color:var(--turmeric)] text-[color:var(--navy-deep)] hover:brightness-95 focus:ring-[color:var(--navy)] font-bold shadow-[0_0_12px_rgba(242,167,27,.35)]",
    danger: "bg-[color:var(--tamarind)] text-white hover:brightness-95 focus:ring-[color:var(--tamarind)]",
    ghost: "text-[color:var(--ink-soft)] hover:bg-[color:var(--turmeric-soft)] hover:text-[color:var(--ink)] focus:ring-[color:var(--line)]",
    success: "bg-[color:var(--chutney)] text-white hover:brightness-95 focus:ring-[color:var(--chutney)]",
  };
  return (
    <As className={`${base} ${variants[variant]} ${className}`} {...props}>
      {children}
    </As>
  );
}

export function Card({ className = "", accent = false, children, ...props }) {
  const accentCls = accent ? "border-t-[3px] border-t-[color:var(--turmeric)]" : "";
  return (
    <div className={`bg-[color:var(--card)] border border-[color:var(--line)] rounded-[14px] shadow-sm ${accentCls} ${className}`} {...props}>
      {children}
    </div>
  );
}

export function Field({ label, children, className = "" }) {
  return (
    <label className={`flex flex-col gap-1.5 ${className}`}>
      {label && <span className="text-xs font-semibold uppercase tracking-wider text-zinc-500">{label}</span>}
      {children}
    </label>
  );
}

export function Input(props) {
  return (
    <input
      {...props}
      className={`flex h-10 w-full rounded-md border border-zinc-300 bg-white px-3 py-2 text-sm placeholder:text-zinc-400 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-orange-700 focus:border-transparent disabled:cursor-not-allowed disabled:opacity-50 ${props.className || ""}`}
    />
  );
}

export function NumInput(props) {
  return <Input type="number" step="any" {...props} className={`text-right font-mono-num ${props.className || ""}`} />;
}

// Password input with an eye toggle to peek at what's been typed.
// Behaves identically to Input for controlled forms — same props (value, onChange, required, ...).
export function PasswordInput({ className = "", ...props }) {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <input
        {...props}
        type={show ? "text" : "password"}
        className={`flex h-10 w-full rounded-md border border-zinc-300 bg-white pl-3 pr-10 py-2 text-sm placeholder:text-zinc-400 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-orange-700 focus:border-transparent disabled:cursor-not-allowed disabled:opacity-50 ${className}`}
      />
      <button
        type="button"
        onClick={() => setShow((v) => !v)}
        tabIndex={-1}
        aria-label={show ? "Hide password" : "Show password"}
        data-testid={props["data-testid"] ? `${props["data-testid"]}-toggle` : "password-toggle"}
        className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-md text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 transition-colors"
      >
        {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
      </button>
    </div>
  );
}

export function Select(props) {
  return (
    <select
      {...props}
      className={`flex h-10 w-full rounded-md border border-zinc-300 bg-white px-3 py-2 text-sm transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-orange-700 focus:border-transparent ${props.className || ""}`}
    >
      {props.children}
    </select>
  );
}

export function StatusPill({ status, label }) {
  const map = {
    verified: "bg-green-100 text-green-800",
    pending: "bg-yellow-100 text-yellow-800",
    correction: "bg-red-100 text-red-800",
    error: "bg-red-100 text-red-800",
  };
  const labels = { verified: "Verified", pending: "Pending", correction: "Correction", error: "Error" };
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${map[status] || "bg-zinc-100 text-zinc-700"}`}>
      {label || labels[status] || status}
    </span>
  );
}

export function PageHeader({ title, subtitle, actions }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6 pb-4 border-b border-[color:var(--line)] relative">
      <div>
        <h1 className="font-heading text-3xl font-extrabold tracking-tight text-[color:var(--ink)] relative inline-block">
          {title}
          <span aria-hidden className="absolute -bottom-1 left-0 h-[3px] w-10 rounded-full bg-[color:var(--turmeric)]" />
        </h1>
        {subtitle && <p className="text-sm text-[color:var(--ink-soft)] mt-3">{subtitle}</p>}
      </div>
      {actions && <div className="flex items-center gap-2 flex-wrap">{actions}</div>}
    </div>
  );
}

export function Stat({ label, value, hint, accent = "", testId }) {
  const accentColor = accent.includes("green") ? "var(--chutney)"
    : accent.includes("red") || accent.includes("yellow") ? "var(--tamarind)"
    : accent.includes("orange") ? "var(--turmeric)" : "var(--ink)";
  return (
    <Card accent className="p-5 flex flex-col items-start gap-1.5 rise-in transition-transform duration-200 hover:-translate-y-0.5" data-testid={testId}>
      <span className="text-[11px] font-heading font-semibold uppercase tracking-widest text-[color:var(--ink-soft)]">{label}</span>
      <span className="font-mono-num text-2xl md:text-3xl font-bold tracking-tight break-all" style={{ color: accentColor }}>{value}</span>
      {hint && <span className="text-xs text-[color:var(--ink-soft)]">{hint}</span>}
    </Card>
  );
}

export function PrintBrand({ reportTitle, period, storeName, preparedBy }) {
  // Professional print/PDF header shown only when printing. Falls back to the
  // minimal brand row when the page didn't pass any report metadata.
  const generatedAt = new Date().toLocaleString("en-IN", {
    day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
  });
  return (
    <div className="print-only mb-5 pb-3 border-b-2 border-zinc-800">
      <div className="flex items-center gap-3">
        <img src="/logo.webp" alt="SSVP" className="h-16 w-16 rounded-full object-cover ring-1 ring-zinc-300" />
        <div className="flex-1 leading-tight">
          <div className="font-heading font-extrabold text-xl text-zinc-950 tracking-tight">Shree Siddhivinayak Vada Pav</div>
          <div className="text-[11px] uppercase tracking-widest text-zinc-500">SSVP Account Pro · Multi-store QSR Accounting</div>
        </div>
      </div>
      {reportTitle && (
        <div className="mt-3 flex items-end justify-between flex-wrap gap-3">
          <div>
            <div className="font-heading font-bold text-2xl text-zinc-950 leading-none">{reportTitle}</div>
            {period && <div className="mt-1 text-xs text-zinc-600">Period: <span className="font-semibold text-zinc-800">{period}</span>{storeName ? <> · Store: <span className="font-semibold text-zinc-800">{storeName}</span></> : null}</div>}
          </div>
          <div className="text-[10px] text-zinc-500 text-right leading-snug">
            <div>Generated <span className="font-mono">{generatedAt}</span></div>
            {preparedBy && <div>Prepared by <span className="font-semibold text-zinc-700">{preparedBy}</span></div>}
          </div>
        </div>
      )}
    </div>
  );
}

export function HRModal({ title, onClose, children, wide = false }) {
  React.useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose} data-testid="hr-modal">
      <div className={`w-full ${wide ? "max-w-3xl" : "max-w-lg"} max-h-[90vh] overflow-y-auto rounded-lg bg-white shadow-xl`} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-100 sticky top-0 bg-white">
          <h3 className="font-heading font-semibold text-zinc-900">{title}</h3>
          <button data-testid="hr-modal-close" onClick={onClose} className="text-zinc-400 hover:text-zinc-700 text-xl leading-none">&times;</button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}


export function ConfirmDialog({ open, title = "Confirm delete", message, confirmWord = "confirm", confirmLabel = "Delete", onConfirm, onClose }) {
  const [text, setText] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  React.useEffect(() => { if (!open) { setText(""); setBusy(false); } }, [open]);
  if (!open) return null;
  const match = text.trim().toLowerCase() === confirmWord.toLowerCase();
  const run = async () => {
    if (!match) return;
    setBusy(true);
    try { await onConfirm?.(); onClose?.(); }
    catch (e) { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="confirm-dialog">
      <div className="w-full max-w-md rounded-lg bg-white shadow-xl">
        <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-100">
          <h3 className="font-heading font-bold text-lg text-zinc-950">{title}</h3>
          <button onClick={onClose} className="p-1 rounded-md hover:bg-zinc-100" data-testid="confirm-close"><X className="h-5 w-5 text-zinc-500" /></button>
        </div>
        <div className="px-5 py-4 space-y-4">
          <div className="flex items-start gap-3 rounded-md border border-red-200 bg-red-50 px-4 py-3">
            <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-800">{message}</p>
          </div>
          <div>
            <label className="text-sm font-medium text-zinc-700">Type <span className="font-mono font-bold">{confirmWord}</span> to confirm</label>
            <input
              data-testid="confirm-input"
              autoFocus
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") run(); }}
              placeholder={confirmWord}
              className="mt-1.5 flex h-10 w-full rounded-md border border-zinc-300 bg-white px-3 py-2 text-sm placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-red-600 focus:border-transparent"
            />
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-zinc-100">
          <Btn variant="secondary" onClick={onClose} disabled={busy}>Cancel</Btn>
          <Btn data-testid="confirm-delete-btn" variant="danger" onClick={run} disabled={!match || busy}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <AlertTriangle className="h-4 w-4" />}
            {confirmLabel}
          </Btn>
        </div>
      </div>
    </div>
  );
}
